import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { ProjectService, UserProject } from '../services/projectService';
import { generateProjectTasks } from '../services/aiAgentService';

export interface ProjectState {
  projects: UserProject[];
  isLoading: boolean;
  isCreating: boolean;
  error: string | null;
}

const initialState: ProjectState = {
  projects: [],
  isLoading: false,
  isCreating: false,
  error: null,
};

export const fetchProjects = createAsyncThunk(
  'projects/fetchProjects',
  async () => {
    return await ProjectService.listProjects();
  }
);

export const createProjectWithTasks = createAsyncThunk(
  'projects/createProjectWithTasks',
  async (
    input: { title: string; description: string; imageUrl?: string; organizationId?: string },
    { rejectWithValue }
  ) => {
    // 1. Save the project
    const project = await ProjectService.createProject({
      title: input.title,
      description: input.description,
      imageUrl: input.imageUrl,
      organizationId: input.organizationId,
    });

    // 2. Call the AI agent to generate 5 tasks
    let tasks;
    try {
      tasks = await generateProjectTasks(input.title, input.description);
    } catch (err) {
      // If AI fails, still return the project with no tasks
      console.error('[createProjectWithTasks] Task generation failed:', err);
      return { ...project, tasks: { items: [] } };
    }

    // 3. Save all tasks in parallel
    const results = await Promise.allSettled(
      tasks.map((task, i) =>
        ProjectService.createProjectTask({
          projectId: project.id,
          title: task.title,
          description: task.description,
          order: i + 1,
        })
      )
    );

    const createdTasks = results.flatMap((result, i) => {
      if (result.status === 'fulfilled') return [result.value];
      console.error(`[createProjectWithTasks] Failed to save task ${i + 1}:`, result.reason);
      return [];
    });

    return { ...project, tasks: { items: createdTasks } };
  }
);

export const updateProject = createAsyncThunk(
  'projects/updateProject',
  async (input: { id: string; imageUrl?: string; title?: string; description?: string; datasetIds?: string[] }) => {
    return await ProjectService.updateProject(input);
  }
);

export const deleteProject = createAsyncThunk(
  'projects/deleteProject',
  async (id: string, { rejectWithValue }) => {
    try {
      await ProjectService.deleteProject(id);
      return id;
    } catch (err) {
      return rejectWithValue(
        err instanceof Error ? err.message : 'Failed to delete project'
      );
    }
  }
);

const projectSlice = createSlice({
  name: 'projects',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    // fetchProjects
    builder.addCase(fetchProjects.pending, (state) => {
      state.isLoading = true;
      state.error = null;
    });
    builder.addCase(fetchProjects.fulfilled, (state, action) => {
      state.isLoading = false;
      state.projects = action.payload;
    });
    builder.addCase(fetchProjects.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.error.message || 'Failed to fetch projects';
    });

    // createProjectWithTasks
    builder.addCase(createProjectWithTasks.pending, (state) => {
      state.isCreating = true;
      state.error = null;
    });
    builder.addCase(createProjectWithTasks.fulfilled, (state, action) => {
      state.isCreating = false;
      state.projects.unshift(action.payload);
    });
    builder.addCase(createProjectWithTasks.rejected, (state, action) => {
      state.isCreating = false;
      state.error = action.error.message || 'Failed to create project';
    });

    // updateProject
    builder.addCase(updateProject.fulfilled, (state, action) => {
      const idx = state.projects.findIndex((p) => p.id === action.payload.id);
      if (idx !== -1) {
        state.projects[idx] = { ...state.projects[idx], ...action.payload };
      }
    });

    // deleteProject
    builder.addCase(deleteProject.pending, (state) => {
      state.error = null;
    });
    builder.addCase(deleteProject.fulfilled, (state, action) => {
      state.projects = state.projects.filter((p) => p.id !== action.payload);
    });
    builder.addCase(deleteProject.rejected, (state, action) => {
      state.error =
        (action.payload as string) ||
        action.error.message ||
        'Failed to delete project';
    });
  },
});

export default projectSlice.reducer;
